import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { IApiResponse } from '@core/interfaces/api-response.interface';
import { map, Observable } from 'rxjs';
import {
  ISocialContentFile,
  ISocialContentFileApi,
  ISocialContentFileMetadataApi,
  ISocialContentFilesDeleteParams,
  ISocialContentFilesFetchParams,
  ISocialContentFilesMetadataParams,
  ISocialContentFilesUploadParams,
  TSocialContentFileListResponse,
} from './social-content-files.interface';

@Injectable({
  providedIn: 'root',
})
export class SocialContentFilesService {
  private readonly http = inject(HttpClient);

  public getFiles(params: ISocialContentFilesFetchParams): Observable<IApiResponse<ISocialContentFile[]>> {
    const httpParams = this.getFilesParams(params);
    const request$ = params.creatorBriefingMode
      ? this.http.get<TSocialContentFileListResponse>(`/files/social-content-files/briefing/${params.briefingId}`, {
          params: httpParams,
        })
      : this.http.get<TSocialContentFileListResponse>('/files/social-content-files', { params: httpParams });

    return request$.pipe(
      map((response) => {
        const files = this.getResponseData(response)
          .map((file) => this.mapSocialContentFile(file))
          .filter((file) => this.matchesCurrentSocialContent(file, params));

        return {
          ...(Array.isArray(response)
            ? { meta: { page: 1, pageSize: files.length, timestamp: '', total: files.length } }
            : response),
          data: files,
        };
      }),
    );
  }

  public uploadFile(params: ISocialContentFilesUploadParams): Observable<ISocialContentFile> {
    const formData = new FormData();
    formData.append('file', params.file);

    const request$ = params.creatorBriefingMode
      ? this.http.post<ISocialContentFileApi>(
          `/files/social-contents/${params.socialContentId}/briefing/${params.briefingId}`,
          formData,
        )
      : this.http.post<ISocialContentFileApi>(
          `/files/social-contents/${params.socialContentId}/campaigns/${params.campaignId}`,
          formData,
        );

    return request$.pipe(map((file) => this.mapSocialContentFile(file)));
  }

  public deleteFile(params: ISocialContentFilesDeleteParams): Observable<void> {
    const url = params.creatorBriefingMode
      ? `/files/social-content-files/${params.socialContentFileId}/briefing/${params.briefingId}`
      : `/files/social-content-files/${params.socialContentFileId}`;

    return this.http.delete<void>(url);
  }

  // Dedicated metadata endpoints added in CampaignManagementAPI@7bad073 - JSON, not blob bytes, so
  // (unlike getFile/getImageUrlAndSize) there's no separate byte fetch happening here at all. The
  // API interceptor recognizes the trailing /metadata path and camelCases the response despite it
  // living under /files/ (see ApiInterceptor.isJsonFileEndpoint).
  public getFileMetadata(params: ISocialContentFilesMetadataParams): Observable<ISocialContentFileMetadataApi> {
    const url = params.creatorBriefingMode
      ? `/files/social-content-files/${params.socialContentFileId}/briefing/${params.briefingId}/metadata`
      : `/files/social-content-files/${params.socialContentFileId}/metadata`;

    return this.http.get<ISocialContentFileMetadataApi>(url);
  }

  private mapSocialContentFile(file: ISocialContentFileApi): ISocialContentFile {
    return {
      campaignId: file.campaignId ?? '',
      createdAt: file.createdAt ?? '',
      createdBy: file.createdBy ?? null,
      fileId: file.fileId,
      id: file.id,
      mimeType: file.mimeType ?? null,
      originalFilename: file.originalFilename ?? null,
      sizeBytes: file.sizeBytes ?? null,
      socialContentId: file.socialContentId ?? '',
    };
  }

  private getFilesParams(params: ISocialContentFilesFetchParams): HttpParams {
    if (params.creatorBriefingMode) {
      return new HttpParams().set('limit', '1000');
    }

    return new HttpParams()
      .set('campaign_id', params.campaignId)
      .set('social_content_id', params.socialContentId)
      .set('limit', '1000');
  }

  private getResponseData(response: TSocialContentFileListResponse): ISocialContentFileApi[] {
    return Array.isArray(response) ? response : (response.data ?? []);
  }

  private matchesCurrentSocialContent(file: ISocialContentFile, params: ISocialContentFilesFetchParams): boolean {
    return (
      (!file.campaignId || file.campaignId === params.campaignId) &&
      (!file.socialContentId || file.socialContentId === params.socialContentId)
    );
  }
}
